import Chart from "chart.js/auto";
import { createMovement, deleteMovement, fetchMovements } from "../lib/api.js";
import { monthlyMovementTotals } from "../lib/aggregate.js";
import { formatCurrency, formatDate, monthLabel, toLocalISODate, parseAmount } from "../lib/format.js";
import { confirmDialog, escapeHtml, openModal, showError, wireAmountInputs } from "../lib/ui.js";

export async function renderMovements(container) {
  container.innerHTML = `<h2>Ingresos y egresos</h2><p>Cargando…</p>`;

  try {
    const movements = await fetchMovements();
    const monthly = monthlyMovementTotals(movements, 8);
    const reload = () => renderMovements(container);

    container.innerHTML = `
      <div id="view">
      <h2>Ingresos y egresos</h2>
      <div class="toolbar">
        <span>${movements.length} movimiento(s)</span>
        <button class="btn" id="new-movement">+ Nuevo movimiento</button>
      </div>

      <div class="panel">
        <h3>Ingresos vs. egresos por mes</h3>
        <div class="chart-wrap"><canvas id="movements-chart"></canvas></div>
      </div>

      <div class="panel">
        ${
          movements.length === 0
            ? "<p>Todavía no hay movimientos registrados.</p>"
            : `<div class="table-wrap"><table class="table-cards">
                <thead><tr><th>Fecha</th><th>Tipo</th><th>Categoría</th><th>Descripción</th><th>Monto</th><th>Origen</th><th></th></tr></thead>
                <tbody>
                  ${movements
                    .map(
                      (m) => `<tr>
                        <td data-label="Fecha">${formatDate(m.date)}</td>
                        <td data-label="Tipo"><span class="badge" style="background: ${m.type === "ingreso" ? "var(--success)" : "var(--danger)"}">${m.type}</span></td>
                        <td data-label="Categoría">${escapeHtml(m.category ?? "—")}</td>
                        <td data-label="Descripción">${escapeHtml(m.description ?? "—")}</td>
                        <td data-label="Monto" class="${m.type === "ingreso" ? "value positive" : "value negative"}" style="font-size: 14px">${formatCurrency(m.amount)}</td>
                        <td data-label="Origen">${m.auto_generated ? "Automático (evento)" : "Manual"}</td>
                        <td data-label="">${m.auto_generated ? "" : `<button class="icon-btn danger" data-delete="${escapeHtml(m.id)}">Borrar</button>`}</td>
                      </tr>`
                    )
                    .join("")}
                </tbody>
              </table></div>`
        }
      </div>
      </div>
    `;

    const view = container.querySelector("#view");

    new Chart(view.querySelector("#movements-chart"), {
      type: "bar",
      data: {
        labels: monthly.map((m) => monthLabel(m.year, m.month)),
        datasets: [
          { label: "Ingresos", data: monthly.map((m) => m.ingreso), backgroundColor: "#21a179" },
          { label: "Egresos", data: monthly.map((m) => m.egreso), backgroundColor: "#e0554c" },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { y: { beginAtZero: true } },
      },
    });

    view.querySelector("#new-movement").addEventListener("click", () => openMovementModal(reload));

    view.addEventListener("click", async (e) => {
      const deleteId = e.target.dataset?.delete;
      if (deleteId && confirmDialog("¿Borrar este movimiento?")) {
        await deleteMovement(deleteId);
        reload();
      }
    });
  } catch (error) {
    showError(container, error);
  }
}

function openMovementModal(onSaved) {
  const modal = openModal(`
    <button class="modal-close" type="button" data-close>✕</button>
    <h3>Nuevo movimiento</h3>
    <form id="movement-form">
      <div class="form-grid">
        <div class="field">
          <label for="m-type">Tipo</label>
          <select id="m-type" name="type">
            <option value="ingreso">Ingreso</option>
            <option value="egreso">Egreso</option>
          </select>
        </div>
        <div class="field">
          <label for="m-amount">Monto</label>
          <input type="text" inputmode="decimal" id="m-amount" name="amount" data-amount required />
        </div>
        <div class="field">
          <label for="m-date">Fecha</label>
          <input type="date" id="m-date" name="date" required value="${toLocalISODate()}" />
        </div>
        <div class="field">
          <label for="m-category">Categoría</label>
          <input type="text" id="m-category" name="category" />
        </div>
        <div class="field full">
          <label for="m-description">Descripción</label>
          <input type="text" id="m-description" name="description" />
        </div>
      </div>
      <p class="error-text" id="movement-error" hidden></p>
      <div class="modal-actions">
        <button type="button" class="btn secondary" data-close>Cancelar</button>
        <button type="submit" class="btn">Guardar</button>
      </div>
    </form>
  `);

  modal.querySelectorAll("[data-close]").forEach((btn) => btn.addEventListener("click", () => modal.close()));
  wireAmountInputs(modal);

  const form = modal.querySelector("#movement-form");
  const errorEl = modal.querySelector("#movement-error");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.hidden = true;
    try {
      await createMovement({
        type: form.type.value,
        amount: parseAmount(form.amount.value),
        date: form.date.value,
        category: form.category.value.trim() || null,
        description: form.description.value.trim() || null,
        auto_generated: false,
      });
      modal.close();
      onSaved?.();
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  });
}
